import { usePermissions } from '@/contexts/PermissionsContext';
import { inboundEmailsService, type InboundEmail } from '@/services/inboundEmailsService';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const INBOUND_EMAILS_KEY = ['inbound-emails'] as const;
export const INBOUND_UNREAD_COUNTS_KEY = ['inbound-unread-counts'] as const;

/** Réponses du client rattachées à un message Contact ('contact') ou à une demande SAV du site ('sav'). */
export function useInboundEmails(kind: 'contact' | 'sav', id: string | undefined) {
  const { hasPermission } = usePermissions();
  const allowed = hasPermission(kind === 'contact' ? 'contact_messages' : 'sav_requests');
  return useQuery({
    queryKey: [...INBOUND_EMAILS_KEY, kind, id],
    queryFn: () =>
      kind === 'contact'
        ? inboundEmailsService.listForContactMessage(id as string)
        : inboundEmailsService.listForSavRequest(id as string),
    enabled: !!id && allowed,
    retry: 1,
  });
}

/** Les liens signés durent 5 minutes : renouvelés avant l'expiration tant que le fil reste ouvert. */
const PHOTO_RENEW_MS = 4 * 60_000;

/**
 * Photos jointes aux réponses d'une demande SAV (liens signés). Jamais en cache (gcTime 0) ; en cas d'erreur (ou si la fonction n'est
 * pas disponible), le fil s'affiche sans photos.
 */
export function useInboundPhotos(webRequestId: string | undefined) {
  const { hasPermission } = usePermissions();
  return useQuery({
    queryKey: [...INBOUND_EMAILS_KEY, 'photos', webRequestId],
    queryFn: () => inboundEmailsService.getSavPhotoLinks(webRequestId as string),
    enabled: !!webRequestId && hasPermission('sav_requests'),
    staleTime: 0,
    gcTime: 0,
    refetchInterval: PHOTO_RENEW_MS,
    retry: false,
  });
}

/**
 * Réponses non lues par message / demande (actualisé toutes les 60 s). AUCUNE requête sans l'un des deux droits
 * (contact_messages ou sav_requests) ; la RLS ne renvoie que ce que l'utilisateur a le droit de voir.
 */
export function useInboundUnreadCounts() {
  const { hasPermission, isLoading } = usePermissions();
  const allowed =
    !isLoading && (hasPermission('contact_messages') || hasPermission('sav_requests'));
  const query = useQuery({
    queryKey: INBOUND_UNREAD_COUNTS_KEY,
    queryFn: () => inboundEmailsService.getUnreadCounts(),
    enabled: allowed,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  return { counts: query.data, isError: query.isError };
}

/** Marque des réponses comme lues puis actualise les listes et les badges. */
export function useMarkInboundRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => inboundEmailsService.markRead(ids),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: INBOUND_EMAILS_KEY });
      queryClient.invalidateQueries({ queryKey: INBOUND_UNREAD_COUNTS_KEY });
    },
  });
}

/**
 * Ouverture d'un fil : mémorise les réponses NON LUES à l'instant de l'ouverture (la pastille « Nouvelle réponse » reste visible
 * pendant la consultation) et les marque comme lues. `open` : le fil est affiché (fenêtre ouverte, détail sélectionné).
 */
export function useMarkNewInboundOnOpen(
  open: boolean,
  emails: InboundEmail[] | undefined,
  resetKey: string | undefined
) {
  const mark = useMarkInboundRead();
  const [newIds, setNewIds] = useState<string[]>([]);
  const done = useRef<string | null>(null);
  const markRef = useRef(mark.mutate);
  markRef.current = mark.mutate;

  useEffect(() => {
    done.current = null;
    setNewIds([]);
  }, [resetKey]);

  useEffect(() => {
    if (!open) {
      done.current = null;
      return;
    }
    if (!emails || done.current === (resetKey ?? '')) return;
    done.current = resetKey ?? '';
    const unread = emails.filter(e => !e.read_at).map(e => e.id);
    setNewIds(unread);
    if (unread.length) markRef.current(unread);
  }, [open, emails, resetKey]);

  const isNew = useCallback((id: string) => newIds.includes(id), [newIds]);
  return useMemo(() => ({ newIds, isNew }), [newIds, isNew]);
}
